import { PackageRatePatternIcon } from '@/components/common/icon';
import { filterLinkedInLegacyPostFallback } from '@/biz/contentType';
import {
  getCreatorPackageAccountConflicts,
  type CreatorCollaborationPackage,
  type CreatorPlacement
} from '@/biz/multiPlatformCollaboration/model';
import {
  normalizeMoneyInputDraft,
  type PackageRates
} from '@/biz/multiPlatformCollaboration/packageRates';
import type { CreatorPackagePriceRecommendationResponse } from '@/interface/creatorOrderV2';
import {
  formatSingleBraceTemplate,
  getCreatorPlacementContentTypeLabel
} from '@/biz/multiPlatformCollaboration/presentation';
import { getPlatformName, PlatformIconComponent } from '@/biz/platform';
import {
  ChevronStrokeIcon,
  CreatorEyeIcon,
  CreatorUserCheckIcon
} from '@/components/common/icon';
import CreatorAccountCascadePicker from '@/components/multiPlatformCollaboration/CreatorAccountCascadePicker';
import CreatorContentTypeSelect, {
  type CreatorContentTypeOption
} from '@/components/multiPlatformCollaboration/CreatorContentTypeSelect';
import CreatorQuoteDesignIcon from '@/components/multiPlatformCollaboration/CreatorQuoteDesignIcon';
import CreatorPriceLoadingRegion from './CreatorPriceLoadingRegion';
import CreatorQuoteCollapse from './CreatorQuoteCollapse';
import EstimateFairRange from '@/components/web/components/EstimatePriceComponents/EstimateFairRange';
import FairRangeEstimateHint from '@/components/web/components/FairRangeEstimateHint';
import { useLoginStore } from '@/hooks/useLoginStore';
import { Alert } from 'aha-components';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { useTranslation } from 'react-i18next';

const PLATFORM_ROW_STYLES: Record<string, string> = {
  youtube: 'bg-[#FEF2F2] border-[#FECACA]',
  tiktok: 'bg-[#ECFDF5] border-[#A7F3D0]',
  linkedin: 'bg-[#EFF6FF] border-[#BFDBFE]',
  instagram: 'bg-[#FAF5FF] border-[#E9D5FF]'
};

const formatCompactNumber = (value: unknown) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  if (number >= 1_000_000) return `${(number / 1_000_000).toFixed(1)}M`;
  if (number >= 1_000) return `${(number / 1_000).toFixed(1)}K`;
  return String(Math.round(number));
};

const formatWholeDollar = (value: number) =>
  `$${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
  }).format(value)}`;

function PlacementRateHint({
  low,
  high,
  rate
}: {
  low: number;
  high: number;
  rate: number;
}) {
  const { t } = useTranslation();
  if (!Number.isFinite(rate) || rate <= 0) return null;
  const below = rate < low;
  const above = rate > high;
  return (
    <Alert
      type={above ? 'warning' : 'success'}
      description={
        below
          ? t('price.fields.set_your_rate.fair_tips.below_fair_range_confirm', {
              defaultValue:
                "You're well placed to land this one. Note that your quote is below the fair price range for this deal, so you may want to take that into account before confirming."
            })
          : above
            ? t('price.fields.set_your_rate.fair_tips.above_fair_range_s3', {
                defaultValue:
                  'Your quote is above the recommended price range for this deal. Lowering it to within the recommended price range would give you the best chance of landing the collaboration.'
              })
            : t('multi_platform.quote.fair_range_success', {
                defaultValue:
                  'Your offer sits within the fair range. A balanced number with a good chance of closing.'
              })
      }
    />
  );
}

const playRateBoundaryAnimation = (element?: HTMLDivElement) => {
  if (
    !element?.animate ||
    (typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)
  ) {
    return;
  }

  element.getAnimations().forEach((animation) => animation.cancel());
  element.animate(
    [
      { transform: 'translateX(0)' },
      { offset: 0.12, transform: 'translateX(-5px)' },
      { offset: 0.25, transform: 'translateX(5px)' },
      { offset: 0.38, transform: 'translateX(0)' },
      { offset: 0.5, transform: 'translateX(0)' },
      { offset: 0.62, transform: 'translateX(-5px)' },
      { offset: 0.75, transform: 'translateX(5px)' },
      { offset: 0.88, transform: 'translateX(0)' },
      { transform: 'translateX(0)' }
    ],
    {
      duration: 480,
      easing: 'ease-in-out'
    }
  );
};

const normalizeContentType = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const getPlacementPriceFields = (placement: CreatorPlacement) => {
  const fields =
    placement.raw?.interestedPriceFields ?? placement.raw?.priceFields ?? [];
  return Array.isArray(fields) ? fields : [];
};

const getFairRange = (
  placement: CreatorPlacement,
  selectedContentType: string
) => {
  const priceField =
    getPlacementPriceFields(placement).find(
      (field: Record<string, any>) =>
        normalizeContentType(field?.contentType) ===
        normalizeContentType(selectedContentType)
    ) ??
    getPlacementPriceFields(placement)[0] ??
    {};
  const suggestion =
    placement.raw?.fairPriceSuggestion ?? priceField?.fairPriceSuggestion ?? {};
  const low = Number(suggestion?.creatorLowActualGet ?? suggestion?.low);
  const high = Number(suggestion?.creatorHighActualGet ?? suggestion?.high);
  return {
    low,
    high
  };
};

const getContentTypeDescription = (
  platform: string,
  contentType: string,
  t: ReturnType<typeof useTranslation>['t']
) => {
  const normalizedPlatform = String(platform ?? '').toLowerCase();
  const normalized = normalizeContentType(contentType);
  if (normalized.includes('integrated')) {
    return t('multi_platform.quote.youtube_integrated_description', {
      defaultValue:
        'Videos that naturally incorporate brands or products into daily content.'
    });
  }
  if (normalized.includes('dedicated')) {
    return t('multi_platform.quote.youtube_dedicated_description', {
      defaultValue:
        'Exclusive promotional videos custom-made specifically for the brand.'
    });
  }

  if (normalizedPlatform === 'tiktok') {
    return t('multi_platform.quote.tiktok_video_description', {
      defaultValue:
        'Exclusive TikTok video custom-made specifically for the brand.'
    });
  }

  if (normalizedPlatform === 'instagram') {
    if (normalized.includes('reel')) {
      return t('multi_platform.quote.instagram_reel_description', {
        defaultValue:
          'A short Instagram video created to exclusively promote a brand.'
      });
    }
    return t('multi_platform.quote.instagram_carousel_description', {
      defaultValue:
        'A single Instagram post featuring multiple brand photos or videos that users can swipe through.'
    });
  }

  if (normalizedPlatform === 'twitter') {
    return t('multi_platform.quote.twitter_post_description', {
      defaultValue: 'Exclusive X post custom-made specifically for the brand.'
    });
  }

  if (normalizedPlatform === 'linkedin') {
    if (normalized.includes('video')) {
      return t('multi_platform.quote.linkedin_video_description', {
        defaultValue:
          'A professional video post where you speak about or demonstrate the brand — such as use cases, expert opinions, or testimonials.'
      });
    }
    if (normalized.includes('image')) {
      return t('multi_platform.quote.linkedin_image_description', {
        defaultValue:
          'A carousel post featuring branded visuals, infographics, or professional photos.'
      });
    }
    return t('multi_platform.quote.linkedin_post_description', {
      defaultValue: 'A LinkedIn post created to exclusively promote a brand.'
    });
  }

  return '';
};

const getContentTypeOptions = (
  placement: CreatorPlacement,
  t: ReturnType<typeof useTranslation>['t']
): CreatorContentTypeOption[] => {
  const priceFields = getPlacementPriceFields(placement);
  const allowedContentTypes = placement.allowedContentTypes ?? [];
  const rawOptions = allowedContentTypes.length
    ? allowedContentTypes.map((contentType) => {
        const priceField = priceFields.find(
          (field: Record<string, any>) =>
            normalizeContentType(field?.contentType) ===
            normalizeContentType(contentType)
        );
        return {
          contentType,
          price:
            priceField?.price ??
            priceField?.amount ??
            (normalizeContentType(contentType) ===
            normalizeContentType(placement.contentType)
              ? placement.rate
              : undefined)
        };
      })
    : [
        ...priceFields,
        {
          contentType: placement.contentType,
          price: placement.rate
        }
      ];
  const seen = new Set<string>();

  const displayOptions = filterLinkedInLegacyPostFallback(
    placement.platform,
    rawOptions,
    (option) => option?.contentType
  );

  return displayOptions.flatMap((option: Record<string, any>) => {
    const contentType = String(option?.contentType ?? '');
    const key = normalizeContentType(contentType);
    if (!key || seen.has(key)) return [];
    seen.add(key);
    const price = Number(option?.price ?? option?.amount);
    return [
      {
        value: contentType,
        label: getCreatorPlacementContentTypeLabel({
          platform: placement.platform,
          contentType,
          t
        }),
        description: getContentTypeDescription(
          placement.platform,
          contentType,
          t
        ),
        price: Number.isFinite(price) ? price : undefined,
        icon: key.includes('integrated') ? 'file' : 'film'
      } satisfies CreatorContentTypeOption
    ];
  });
};

const getAvatar = (placement: CreatorPlacement) =>
  placement.raw?.avatar ??
  placement.raw?.avatarUrl ??
  placement.raw?.profileImage ??
  placement.raw?.profilePictureLink ??
  placement.raw?.influencer?.avatar ??
  placement.raw?.user?.imageUrl;

export type CreatorPackageAccountEditorProps = {
  collaborationPackage: CreatorCollaborationPackage;
  mobile?: boolean;
  quoteTermsReadOnly?: boolean;
  /** Submitted channel types stay fixed while counteroffer prices remain editable. */
  contentTypesReadOnly?: boolean;
  rates: PackageRates;
  includedPlacementIds: string[];
  expandedPlacementIds: string[];
  selectedContentTypes?: Record<string, string>;
  /** V2 prediction is authoritative; an empty array means no current prediction. */
  channelRecommendations?: CreatorPackagePriceRecommendationResponse['channels'];
  /** First quote: start at zero without an estimate; do not copy catalog prices. */
  initializeFromRecommendation?: boolean;
  recommendationLoading?: boolean;
  loadingPlacementIds?: string[];
  footer?: ReactNode;
  footerError?: ReactNode;
  hidePlacementTotal?: boolean;
  accountsTitle?: string;
  showAccountsTitle?: boolean;
  showAccountSelector?: boolean;
  allowRemove?: boolean;
  showConflictValidation: boolean;
  invalidRatePlacementIds?: string[];
  showPackageRateError?: boolean;
  onRatesChange: (rates: PackageRates) => void;
  onIncludedPlacementIdsChange: (placementIds: string[]) => void;
  onExpandedPlacementIdsChange: (placementIds: string[]) => void;
  onSelectedContentTypesChange?: (contentTypes: Record<string, string>) => void;
};

export default function CreatorPackageAccountEditor({
  collaborationPackage,
  mobile = false,
  quoteTermsReadOnly = false,
  contentTypesReadOnly = false,
  rates,
  includedPlacementIds,
  expandedPlacementIds,
  selectedContentTypes: controlledContentTypes,
  channelRecommendations,
  initializeFromRecommendation = false,
  recommendationLoading = false,
  loadingPlacementIds = [],
  footer,
  footerError,
  hidePlacementTotal = false,
  accountsTitle,
  showAccountsTitle = true,
  showAccountSelector = true,
  allowRemove = true,
  showConflictValidation,
  invalidRatePlacementIds = [],
  showPackageRateError = false,
  onRatesChange,
  onIncludedPlacementIdsChange,
  onExpandedPlacementIdsChange,
  onSelectedContentTypesChange
}: CreatorPackageAccountEditorProps) {
  const { t } = useTranslation();
  const showAccountsHeader =
    showAccountsTitle || (showAccountSelector && !quoteTermsReadOnly);
  const channelAvatar = useLoginStore((state) => state.channelAvatar);
  const accountEditorId = useId();
  const rateDisplayRefs = useRef(new Map<string, HTMLDivElement>());
  const [localContentTypes, setLocalContentTypes] = useState<
    Record<string, string>
  >({});
  const [rateDrafts, setRateDrafts] = useState<Record<string, string>>({});
  const [priceAdjustmentHintRates, setPriceAdjustmentHintRates] = useState<
    Record<string, number | undefined>
  >({});
  useEffect(() => {
    setPriceAdjustmentHintRates((current) => {
      const changedIds = Object.keys(current).filter(
        (id) => current[id] !== undefined && current[id] !== rates[id]
      );
      if (!changedIds.length) return current;
      const next = { ...current };
      changedIds.forEach((id) => delete next[id]);
      return next;
    });
  }, [rates]);
  useEffect(() => {
    if (quoteTermsReadOnly) setRateDrafts({});
  }, [quoteTermsReadOnly]);
  const [openContentTypePlacementId, setOpenContentTypePlacementId] =
    useState<string>();
  const selectedContentTypes = controlledContentTypes ?? localContentTypes;
  const includedSet = useMemo(
    () => new Set(includedPlacementIds),
    [includedPlacementIds]
  );
  const invalidRateSet = useMemo(
    () => new Set(invalidRatePlacementIds),
    [invalidRatePlacementIds]
  );
  const conflictByPlacementId = useMemo(
    () =>
      new Map(
        getCreatorPackageAccountConflicts(collaborationPackage).map(
          (conflict) => [conflict.placement.id, conflict]
        )
      ),
    [collaborationPackage]
  );
  const allIncludedPlacements = collaborationPackage.placements.filter(
    (placement) => includedSet.has(placement.id)
  );
  const includedPlacements = allIncludedPlacements.filter(
    (placement) => !(mobile && placement.raw?.hideInMobileQuotePreview)
  );
  const hasShownConflict =
    showConflictValidation &&
    includedPlacements.some((placement) =>
      conflictByPlacementId.has(placement.id)
    );
  const hasShownError =
    hasShownConflict ||
    showPackageRateError ||
    allIncludedPlacements.some((placement) => invalidRateSet.has(placement.id));

  const toggleExpanded = (placementId: string) => {
    onExpandedPlacementIdsChange(
      expandedPlacementIds.includes(placementId) ? [] : [placementId]
    );
  };

  const removePlacement = (placementId: string) => {
    if (quoteTermsReadOnly || !allowRemove) return;
    onIncludedPlacementIdsChange(
      includedPlacementIds.filter((id) => id !== placementId)
    );
    onExpandedPlacementIdsChange(
      expandedPlacementIds.filter((id) => id !== placementId)
    );
  };

  return (
    <section className="w-full">
      {showAccountsHeader ? (
        <div className="relative flex h-6 items-center justify-between gap-4 max-md:h-auto max-md:flex-col max-md:items-stretch max-md:gap-4">
          {showAccountsTitle ? (
            <h3 className="m-0 text-base font-semibold leading-6 text-[#1C1917]">
              {accountsTitle ??
                t('multi_platform.quote.select_accounts', {
                  defaultValue: 'Select accounts to include'
                })}
            </h3>
          ) : null}
          {showAccountSelector && !quoteTermsReadOnly ? (
            <CreatorAccountCascadePicker
              collaborationPackage={collaborationPackage}
              includedPlacementIds={includedPlacementIds}
              mobile={mobile}
              onIncludedPlacementIdsChange={onIncludedPlacementIdsChange}
            />
          ) : null}
        </div>
      ) : null}

      <div
        data-testid="creator-package-account-frame"
        className={`${showAccountsHeader ? (mobile ? 'mt-4' : 'mt-3') : ''} rounded-lg bg-white ${
          openContentTypePlacementId ? 'overflow-visible' : 'overflow-hidden'
        } ${hasShownError ? 'border-2 border-[#EF4444]' : 'border border-[#E7E5E4]'}`}
      >
        <div className={`p-[15px] ${hidePlacementTotal ? '' : 'pb-0'}`}>
          <div
            data-testid="creator-package-placement-list"
            className="rounded-md overflow-visible"
          >
            {includedPlacements.map((placement, index) => {
              const expanded = expandedPlacementIds.includes(placement.id);
              const detailsId = `${accountEditorId}-${placement.id}`;
              const contentTypeMenuOpen =
                openContentTypePlacementId === placement.id;
              const removedByBrand = Boolean(
                placement.raw?.disabledReason === 'REMOVED'
              );
              const conflict = conflictByPlacementId.get(placement.id);
              const showConflict = showConflictValidation && Boolean(conflict);
              const showRateError = invalidRateSet.has(placement.id);
              const rowStyle =
                PLATFORM_ROW_STYLES[placement.platform] ??
                'bg-[#FAFAF9] border-[#E7E5E4]';
              const requestedContentType = contentTypesReadOnly
                ? placement.contentType
                : (selectedContentTypes[placement.id] ?? placement.contentType);
              const contentTypeOptions = getContentTypeOptions(
                contentTypesReadOnly
                  ? {
                      ...placement,
                      allowedContentTypes: [placement.contentType ?? '']
                    }
                  : placement,
                t
              );
              const selectedContentType =
                contentTypeOptions.find(
                  (option) =>
                    normalizeContentType(option.value) ===
                    normalizeContentType(requestedContentType)
                )?.value ??
                contentTypeOptions[0]?.value ??
                requestedContentType;
              const contentTypeLabel = getCreatorPlacementContentTypeLabel({
                platform: placement.platform,
                contentType: selectedContentType,
                t
              });
              const accountName = (placement.userName || 'creator').replace(
                /^@+/,
                ''
              );
              const rate = Number(rates[placement.id] ?? 0);
              const hasRate = Number.isFinite(rate) && rate > 0;
              const priceLoading =
                recommendationLoading &&
                loadingPlacementIds.includes(placement.id);
              const rateDraft = quoteTermsReadOnly
                ? undefined
                : rateDrafts[placement.id];
              const showNumericRate = hasRate || initializeFromRecommendation;
              const displayedRate =
                rateDraft ?? (showNumericRate ? String(rate) : '');
              const enterPriceLabel = t(
                'multi_platform.quote.enter_channel_price',
                { defaultValue: 'Enter price' }
              );
              const prediction = channelRecommendations?.find(
                (channel) =>
                  channel.channelKey ===
                    (placement.channelKey || placement.id) &&
                  channel.contentType === selectedContentType
              );
              const { low, high } =
                channelRecommendations === undefined
                  ? getFairRange(placement, selectedContentType)
                  : {
                      low: prediction?.estimatedRange?.min,
                      high: prediction?.estimatedRange?.max
                    };
              const hasFairRange =
                typeof low === 'number' &&
                typeof high === 'number' &&
                Number.isFinite(low) &&
                Number.isFinite(high) &&
                high > low;
              const avatar = getAvatar(placement) || channelAvatar;
              const continuesStack =
                index < includedPlacements.length - 1 || !hidePlacementTotal;

              return (
                <div
                  key={placement.id}
                  className={`relative -mt-px first:mt-0 ${contentTypeMenuOpen ? 'z-10' : ''}`}
                >
                  {continuesStack ? (
                    // Figma 1946:10497: each platform's fill continues behind
                    // the next card's 6px top corners, including the total card.
                    <div
                      aria-hidden="true"
                      data-testid="placement-stack-background"
                      className={`pointer-events-none absolute inset-x-0 top-0 -bottom-[6px] rounded-t-md border border-b-0 ${rowStyle} ${showConflict || showRateError ? '!border-[#EF4444]' : ''}`}
                    />
                  ) : null}
                  <article
                    data-placement-id={placement.id}
                    data-binding-conflict={showConflict || undefined}
                    className={`relative border ${continuesStack ? 'rounded-t-md border-b-0 pb-px' : 'rounded-md'} ${
                      contentTypeMenuOpen
                        ? 'overflow-visible'
                        : 'overflow-hidden'
                    } ${rowStyle} ${
                      showConflict || showRateError ? '!border-[#EF4444]' : ''
                    }`}
                  >
                    <div className="flex min-h-[33px] items-center gap-2 px-[15px] max-md:min-h-[52px] max-md:flex-wrap max-md:py-2">
                      <button
                        type="button"
                        className={`flex min-w-0 flex-1 items-center gap-1 border-0 bg-transparent p-0 text-left max-md:items-start ${
                          removedByBrand
                            ? 'opacity-40 line-through decoration-[#A3A3A3]'
                            : ''
                        }`}
                        aria-expanded={expanded}
                        aria-controls={detailsId}
                        disabled={removedByBrand}
                        onClick={() => toggleExpanded(placement.id)}
                      >
                        <span
                          aria-hidden="true"
                          data-testid="placement-collapse-chevron"
                          className={`flex h-4 w-4 flex-shrink-0 items-center justify-center text-[#525252] transition-transform duration-200 ease-out motion-reduce:transition-none ${
                            expanded ? 'rotate-90' : ''
                          }`}
                        >
                          <ChevronStrokeIcon className="block h-[9.5px] w-[5.5px]" />
                        </span>
                        <span className="flex min-w-0 flex-1 items-center gap-1 max-md:flex-col max-md:items-start max-md:gap-0">
                          <span className="flex min-w-0 items-center gap-1 text-xs font-medium leading-[18px] text-[#292524] max-md:font-normal">
                            <PlatformIconComponent
                              platform={placement.platform}
                              className="h-3.5 w-3.5 flex-shrink-0"
                            />
                            <span className="truncate">{contentTypeLabel}</span>
                          </span>
                          <span
                            aria-hidden
                            className="h-3 w-px flex-shrink-0 bg-[#D6D3D1] max-md:hidden"
                          />
                          <span className="min-w-0 max-w-full truncate text-xs leading-[18px] text-[#79716B]">
                            @{accountName}
                          </span>
                        </span>
                      </button>

                      {showConflict ? (
                        <>
                          <span className="min-w-0 flex-1 truncate text-right text-xs leading-[18px] text-[#DC2626] max-md:order-last max-md:basis-full max-md:text-left">
                            {t('multi_platform.quote.channel_bound_error', {
                              defaultValue:
                                'This channel has been bound by other users'
                            })}
                          </span>
                          {allowRemove && !quoteTermsReadOnly ? (
                            <>
                              <span
                                aria-hidden
                                className="h-2.5 w-px bg-[#D6D3D1] max-md:hidden"
                              />
                              <button
                                type="button"
                                className="flex h-4 w-4 items-center justify-center border-0 bg-transparent p-0 text-[#525252]"
                                aria-label={t(
                                  'multi_platform.quote.remove_channel',
                                  { defaultValue: 'Remove channel' }
                                )}
                                onClick={() => removePlacement(placement.id)}
                              >
                                <CreatorQuoteDesignIcon
                                  name="trash"
                                  className="h-4 w-4"
                                />
                              </button>
                            </>
                          ) : null}
                        </>
                      ) : removedByBrand ? (
                        <span className="whitespace-nowrap text-xs leading-[18px] text-[#737373]">
                          {t('multi_platform.quote.channel_removed_by_brand', {
                            defaultValue:
                              'The channel has been removed by brand'
                          })}
                        </span>
                      ) : (
                        <>
                          <span
                            className={`whitespace-nowrap text-xs leading-[18px] ${
                              showRateError
                                ? 'text-[#DC2626]'
                                : 'text-[#525252]'
                            }`}
                          >
                            {priceLoading ? (
                              t('common.loading', {
                                defaultValue: 'Loading...'
                              })
                            ) : hasRate ? (
                              <>
                                {new Intl.NumberFormat('en-US', {
                                  maximumFractionDigits: 0
                                }).format(rate)}{' '}
                                USD
                              </>
                            ) : (
                              <button
                                type="button"
                                className="cursor-pointer border-0 bg-transparent p-0 text-inherit"
                                aria-expanded={expanded}
                                aria-controls={detailsId}
                                onClick={() => {
                                  if (!expanded) {
                                    onExpandedPlacementIdsChange([
                                      placement.id
                                    ]);
                                  }
                                }}
                              >
                                {enterPriceLabel}
                              </button>
                            )}
                          </span>
                          {allowRemove && !quoteTermsReadOnly ? (
                            <>
                              <span
                                aria-hidden
                                className="h-2.5 w-px bg-[#D6D3D1]"
                              />
                              <button
                                type="button"
                                className="flex h-4 w-4 items-center justify-center border-0 bg-transparent p-0 text-[#525252]"
                                aria-label={t(
                                  'multi_platform.quote.remove_channel',
                                  {
                                    defaultValue: 'Remove channel'
                                  }
                                )}
                                onClick={() => removePlacement(placement.id)}
                              >
                                <CreatorQuoteDesignIcon
                                  name="trash"
                                  className="h-4 w-4"
                                />
                              </button>
                            </>
                          ) : null}
                        </>
                      )}
                    </div>

                    <CreatorQuoteCollapse
                      id={detailsId}
                      open={expanded}
                      overflowVisible={contentTypeMenuOpen}
                    >
                      <div
                        data-testid="placement-expanded-body"
                        className={`mx-[15px] mb-[15px] flex flex-col gap-4 rounded-lg bg-white p-4 ${
                          showConflict
                            ? 'h-auto'
                            : hasFairRange
                              ? 'min-h-[364px] max-md:min-h-0'
                              : 'h-auto'
                        }`}
                      >
                        <div className="flex h-[50px] flex-shrink-0 items-center gap-3 max-md:h-[42px] max-md:gap-2">
                          {avatar ? (
                            <img
                              src={avatar}
                              alt=""
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#FDBA74] to-[#F97316] text-sm font-semibold text-white">
                              {accountName.slice(0, 1).toUpperCase()}
                            </span>
                          )}
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-[#292524]">
                              @{accountName}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#79716B]">
                              <span className="inline-flex items-center gap-1">
                                <span
                                  className="hidden max-md:block"
                                  aria-hidden="true"
                                >
                                  <CreatorUserCheckIcon className="h-4 w-4" />
                                </span>
                                <span className="max-md:hidden">
                                  {t('multi_platform.quote.followers', {
                                    defaultValue: 'Followers'
                                  })}{' '}
                                </span>
                                {formatCompactNumber(
                                  placement.raw?.followerCount ??
                                    placement.raw?.followers
                                )}
                              </span>
                              <span className="h-2.5 w-px bg-[#D6D3D1]" />
                              <span className="inline-flex items-center gap-1">
                                <span
                                  className="hidden max-md:block"
                                  aria-hidden="true"
                                >
                                  <CreatorEyeIcon className="h-4 w-4" />
                                </span>
                                <span className="max-md:hidden">
                                  {t('multi_platform.quote.median_views', {
                                    defaultValue: 'Median views'
                                  })}{' '}
                                </span>
                                {formatCompactNumber(
                                  placement.raw?.medianView ??
                                    placement.raw?.medianViews ??
                                    placement.raw?.medianViewCount
                                )}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="h-px w-full flex-shrink-0 bg-[#E7E5E4]" />

                        {showConflict ? (
                          <div>
                            <p className="m-0 text-xs leading-[18px] text-[#57534E]">
                              {t(
                                'multi_platform.quote.channel_bound_guidance',
                                {
                                  defaultValue:
                                    'If you wish to submit rate, you need to first unbind this channel from that user, or delete this channel.'
                                }
                              )}
                            </p>
                          </div>
                        ) : (
                          <div className="flex min-h-0 flex-1 flex-col gap-6">
                            <div className="flex h-9 flex-shrink-0 items-center justify-between gap-3 max-md:h-[72px] max-md:flex-col max-md:items-stretch max-md:gap-4">
                              <CreatorContentTypeSelect
                                active={expanded}
                                value={selectedContentType}
                                disabled={
                                  quoteTermsReadOnly || contentTypesReadOnly
                                }
                                options={contentTypeOptions}
                                onOpenChange={(open) =>
                                  setOpenContentTypePlacementId(
                                    open ? placement.id : undefined
                                  )
                                }
                                onChange={(option) => {
                                  if (
                                    quoteTermsReadOnly ||
                                    contentTypesReadOnly
                                  )
                                    return;
                                  const nextContentTypes = {
                                    ...selectedContentTypes,
                                    [placement.id]: option.value
                                  };
                                  setLocalContentTypes(nextContentTypes);
                                  onSelectedContentTypesChange?.(
                                    nextContentTypes
                                  );
                                  if (
                                    !initializeFromRecommendation &&
                                    option.price != null
                                  ) {
                                    onRatesChange({
                                      ...rates,
                                      [placement.id]: option.price
                                    });
                                  }
                                }}
                              />
                              {hasFairRange && !recommendationLoading ? (
                                <div className="flex min-h-9 items-center max-md:min-h-5 max-md:w-full">
                                  <FairRangeEstimateHint showMobileChevron />
                                </div>
                              ) : null}
                            </div>

                            <CreatorPriceLoadingRegion
                              loading={priceLoading}
                              disabled={recommendationLoading}
                            >
                              <div className="flex flex-col gap-6">
                                <div
                                  data-testid={`channel-rate-display-${placement.id}`}
                                  ref={(element) => {
                                    if (element) {
                                      rateDisplayRefs.current.set(
                                        placement.id,
                                        element
                                      );
                                    } else {
                                      rateDisplayRefs.current.delete(
                                        placement.id
                                      );
                                    }
                                  }}
                                  className={`flex h-[60px] flex-shrink-0 items-center justify-center ${
                                    mobile
                                      ? 'flex h-[84px] flex-col items-center justify-center gap-1'
                                      : 'gap-2'
                                  }`}
                                >
                                  <label className="flex h-[60px] items-center gap-1">
                                    {showNumericRate ? (
                                      <span className="text-2xl font-semibold leading-8 text-[#44403C]">
                                        $
                                      </span>
                                    ) : (
                                      <CreatorQuoteDesignIcon
                                        name="edit"
                                        className="h-5 w-5 text-[#A9A29D]"
                                      />
                                    )}
                                    <input
                                      type="number"
                                      readOnly={quoteTermsReadOnly}
                                      min={1}
                                      step={1}
                                      value={displayedRate}
                                      placeholder={
                                        showNumericRate
                                          ? undefined
                                          : enterPriceLabel
                                      }
                                      aria-label={formatSingleBraceTemplate(
                                        t('multi_platform.rate_for_platform'),
                                        {
                                          platform: getPlatformName(
                                            placement.platform
                                          )
                                        }
                                      )}
                                      aria-invalid={showRateError}
                                      aria-describedby={
                                        showRateError
                                          ? `channel-rate-error-${placement.id}`
                                          : undefined
                                      }
                                      className={`h-[60px] border-0 bg-transparent p-0 text-[#44403C] outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                                        showNumericRate
                                          ? 'min-w-[89px] max-w-[240px] text-center text-5xl font-semibold leading-[60px] tracking-[-0.96px]'
                                          : 'w-[78px] text-sm font-semibold leading-5 placeholder:text-[#57534E]'
                                      }`}
                                      style={{
                                        width: showNumericRate
                                          ? `${Math.max(3.5, displayedRate.length + 0.5)}ch`
                                          : undefined
                                      }}
                                      onFocus={() => {
                                        if (quoteTermsReadOnly) return;
                                        setRateDrafts((current) => ({
                                          ...current,
                                          [placement.id]: hasRate
                                            ? String(rate)
                                            : ''
                                        }));
                                      }}
                                      onChange={(event) => {
                                        if (quoteTermsReadOnly) return;
                                        const nextDraft =
                                          normalizeMoneyInputDraft(
                                            event.currentTarget.value
                                          );
                                        if (nextDraft !== displayedRate) {
                                          setPriceAdjustmentHintRates(
                                            (current) => ({
                                              ...current,
                                              [placement.id]: undefined
                                            })
                                          );
                                        }
                                        setRateDrafts((current) => ({
                                          ...current,
                                          [placement.id]: nextDraft
                                        }));
                                        onRatesChange({
                                          ...rates,
                                          [placement.id]: Number(nextDraft || 0)
                                        });
                                      }}
                                      onBlur={() =>
                                        setRateDrafts((current) => {
                                          const next = { ...current };
                                          delete next[placement.id];
                                          return next;
                                        })
                                      }
                                    />
                                  </label>
                                  {showNumericRate && !quoteTermsReadOnly ? (
                                    <button
                                      type="button"
                                      className="flex cursor-pointer items-center gap-1 self-center whitespace-nowrap border-0 bg-transparent p-0 text-sm font-semibold leading-5 text-[#57534E] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FB6011]"
                                      onClick={() => {
                                        const input = rateDisplayRefs.current
                                          .get(placement.id)
                                          ?.querySelector('input');
                                        input?.focus();
                                        input?.select();
                                      }}
                                    >
                                      <CreatorQuoteDesignIcon
                                        name="edit"
                                        className="h-5 w-5 text-[#A9A29D]"
                                      />
                                      <span>
                                        {t(
                                          'price.fields.set_your_rate.edit_price',
                                          {
                                            defaultValue: 'Edit price'
                                          }
                                        )}
                                      </span>
                                    </button>
                                  ) : null}
                                </div>

                                {!quoteTermsReadOnly &&
                                hasFairRange &&
                                priceAdjustmentHintRates[placement.id] ===
                                  rate ? (
                                  <p className="m-0 -mt-6 self-center border-t border-[#FF7728] text-center font-['DM_Sans'] text-sm font-normal leading-[22px] tracking-[-0.14px] text-[#FF621D]">
                                    {t(
                                      'price.fields.set_your_rate.adjust_price_hint',
                                      {
                                        defaultValue:
                                          'Click the amount to adjust your price.'
                                      }
                                    )}
                                  </p>
                                ) : null}

                                {showRateError ? (
                                  <p
                                    id={`channel-rate-error-${placement.id}`}
                                    className="m-0 text-center text-xs leading-[18px] text-[#DC2626]"
                                    role="alert"
                                  >
                                    {t('multi_platform.rate_validation', {
                                      defaultValue:
                                        'Enter a valid channel rate.'
                                    })}
                                  </p>
                                ) : null}

                                {hasFairRange ? (
                                  <div
                                    data-testid={`channel-fair-range-${placement.id}`}
                                    className="flex min-h-[106px] flex-shrink-0 flex-col gap-4 max-md:min-h-[146px]"
                                  >
                                    <EstimateFairRange
                                      disabled={quoteTermsReadOnly}
                                      low={low}
                                      high={high}
                                      value={rate}
                                      formatBoundaryPrice={formatWholeDollar}
                                      onReachBoundary={(boundary, nextRate) => {
                                        if (boundary === 'max') {
                                          setPriceAdjustmentHintRates(
                                            (current) => ({
                                              ...current,
                                              [placement.id]: nextRate
                                            })
                                          );
                                        }
                                        playRateBoundaryAnimation(
                                          rateDisplayRefs.current.get(
                                            placement.id
                                          )
                                        );
                                      }}
                                      setValue={(nextRate) => {
                                        if (quoteTermsReadOnly) return;
                                        onRatesChange({
                                          ...rates,
                                          [placement.id]: Number(nextRate)
                                        });
                                      }}
                                    />
                                    <PlacementRateHint
                                      low={low}
                                      high={high}
                                      rate={rate}
                                    />
                                  </div>
                                ) : null}
                              </div>
                            </CreatorPriceLoadingRegion>
                          </div>
                        )}
                      </div>
                    </CreatorQuoteCollapse>
                  </article>
                </div>
              );
            })}
          </div>
        </div>
        {!hidePlacementTotal ? (
          <CreatorPriceLoadingRegion
            loading={recommendationLoading}
            showText={false}
          >
            {footer ? (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-px bottom-0 left-[15px] right-[15px] overflow-hidden rounded-md border border-[#CBD5E1] bg-[#FAFAFA]"
              >
                {/* Figma 1946:10539: visible pattern is 453×115, aligned to the total card's left edge. */}
                <PackageRatePatternIcon
                  width={453}
                  height={115}
                  className="absolute left-0 top-0 h-[115px] w-[453px] max-w-none"
                />
              </div>
            ) : null}
            <div
              className={`relative mx-[15px] flex h-9 items-center justify-between px-[15px] text-xs text-[#171717] ${
                footer
                  ? 'border border-transparent'
                  : 'rounded-md border border-[#D4D4D4] bg-white'
              }`}
            >
              <span>
                {t('multi_platform.quote.placement_total', {
                  defaultValue: 'Placement total'
                })}
              </span>
              <span className="text-sm leading-5 text-[#525252]">
                {new Intl.NumberFormat('en-US', {
                  maximumFractionDigits: 0
                }).format(
                  allIncludedPlacements.reduce(
                    (total, placement) =>
                      total + Number(rates[placement.id] ?? 0),
                    0
                  )
                )}{' '}
                USD
              </span>
            </div>
            {footer}
          </CreatorPriceLoadingRegion>
        ) : null}
      </div>
      {footerError}
    </section>
  );
}
